import { useCallback, useEffect, useState } from 'react';
import {
  listMySessions as customerListSessions,
  revokeAllOtherSessions as customerRevokeAll,
  revokeSession as customerRevokeOne,
  type UserSessionRow,
} from '../../lib/customer-api';
import {
  listMySessions as proListSessions,
  revokeAllOtherSessions as proRevokeAll,
  revokeSession as proRevokeOne,
} from '../../lib/professional-api';
import { toast } from '../../lib/toast';

type Role = 'customer' | 'professional';

type Props = {
  role: Role;
};

/**
 * "Where you're signed in" panel for the Security page. Lists every
 * ACTIVE user_sessions row for the caller, lets them sign out of any
 * individual device, and offers a single button to wipe every other
 * session at once. Mobile-friendly: row layout collapses to two lines
 * on small screens and the actions hang to the right with truncation
 * on the device label.
 *
 * The current session is flagged but the Remove button is suppressed
 * for it — backend rejects revoking your own current session anyway,
 * so we keep the affordance honest. The user logs out through the
 * normal Logout flow if they want to end the current device.
 */
export function SessionList({ role }: Props) {
  const fetcher = role === 'customer' ? customerListSessions : proListSessions;
  const revokeOne = role === 'customer' ? customerRevokeOne : proRevokeOne;
  const revokeAll = role === 'customer' ? customerRevokeAll : proRevokeAll;

  const [rows, setRows] = useState<UserSessionRow[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Client-side pagination — `listMySessions` returns the full set, so
  // we page it here to keep the list short when a pro has signed in
  // from many devices.
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetcher();
      setRows(res.data ?? []);
      setCurrentId(res.current_session_id ?? null);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          'Could not load your active sessions.',
      );
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (row: UserSessionRow) => {
    if (busyId) return;
    setBusyId(row.id);
    setMessage(null);
    // Optimistic — drop the row immediately so the click feels
    // instant. If the call fails we restore the full list.
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    try {
      await revokeOne(row.id);
      setMessage('Device signed out.');
    } catch (err: any) {
      setRows(snapshot);
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          'Could not sign out that device.',
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeAll = async () => {
    const others = rows.filter((r) => !r.is_current);
    if (others.length === 0) return;
    if (bulkBusy) return;

    setBulkBusy(true);
    setMessage(null);
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.is_current));
    try {
      const res = await revokeAll();
      const count = res.revoked_count ?? others.length;
      setMessage(
        `Signed out of ${count} other device${count === 1 ? '' : 's'}.`,
      );
    } catch (err: any) {
      setRows(snapshot);
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          'Could not sign out other devices.',
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const formatWhen = (iso: string | null) => {
    if (!iso) return '';
    try {
      const date = new Date(iso);
      const diffMs = Date.now() - date.getTime();
      const min = Math.floor(diffMs / 60_000);
      if (min < 1) return 'just now';
      if (min < 60) return `${min}m ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h ago`;
      const days = Math.floor(hr / 24);
      if (days < 7) return `${days}d ago`;
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const formatLocation = (row: UserSessionRow) => {
    const place = [row.city, row.country].filter(Boolean).join(', ');
    return place || row.ip_address || 'Unknown location';
  };

  const otherCount = rows.filter((r) => !r.is_current).length;

  // Derive paging from the current row set. Clamp the page when rows
  // shrink (e.g. after revoking the last device on the last page).
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px]">
            Where you're signed in
          </h2>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-0.5">
            Remove any device you don't recognise — it'll be signed out
            immediately.
          </p>
        </div>
        {otherCount > 0 ? (
          <button
            type="button"
            onClick={handleRevokeAll}
            disabled={bulkBusy}
            className="text-[#8e2a2f] text-[13px] font-bold hover:underline disabled:opacity-50"
          >
            {bulkBusy ? 'Signing out…' : 'Sign out everywhere else'}
          </button>
        ) : null}
      </div>

      {message ? (
        <p className="text-[#3a3335] text-[13px] mb-3">{message}</p>
      ) : null}

      {loading ? (
        <p className="text-[#908e8e] text-[14px] py-10 text-center">
          Loading your sessions…
        </p>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-[#eee] rounded-2xl">
          <span className="material-icons text-[#ccc] text-[32px] block mb-2">
            devices
          </span>
          <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold">
            No active sessions
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {pageRows.map((row) => (
            <li
              key={row.id}
              className="flex items-start gap-3 sm:gap-4 border border-[#eee] rounded-xl px-3 sm:px-4 py-3"
            >
              <div className="w-[40px] h-[40px] sm:w-[48px] sm:h-[48px] shrink-0 rounded-[10px] border border-[#ede8e9] bg-white flex items-center justify-center">
                <span className="material-icons text-[#908e8e] text-[20px] sm:text-[22px]">
                  {row.is_mobile ? 'smartphone' : 'laptop'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-2">
                  <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                    {row.device_label || 'Unknown device'}
                  </p>
                  {row.is_current ? (
                    <span className="text-[11px] font-bold text-[#198754]">
                      Current session
                    </span>
                  ) : null}
                </div>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-0.5 truncate">
                  {formatLocation(row)}
                  {row.last_activity_at
                    ? ` · ${formatWhen(row.last_activity_at)}`
                    : ''}
                </p>
              </div>
              {!row.is_current ? (
                <button
                  type="button"
                  onClick={() => handleRevoke(row)}
                  disabled={busyId === row.id}
                  className="text-[#8e2a2f] text-[12px] font-bold hover:underline disabled:opacity-50 whitespace-nowrap"
                >
                  {busyId === row.id ? 'Removing…' : 'Remove device'}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* Pagination — only shown when the session list spills past one
          page. Prev/Next + a "Page X of Y" indicator. */}
      {!loading && totalPages > 1 ? (
        <div className="flex items-center justify-between mt-4">
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
            Page {page} of {totalPages} · {rows.length} device
            {rows.length === 1 ? '' : 's'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#eee] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <span className="material-icons text-[18px]">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#eee] text-[#908e8e] hover:border-[#8e2a2f] hover:text-[#8e2a2f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <span className="material-icons text-[18px]">chevron_right</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
